import { useQuery } from "react-query";
import { zodParseWithLog } from "src/api/apiErrorLogs";
import { loggedInApiCall } from "src/api/axiosClient/userApi";
import { z } from "zod";
export const customThemeQueryKey = "getAllCustomTheme" as const;

type Options = {
    page?: number;
    itemsPerPage?: number;
};

export const useGetCustomThemes = (options?: Options) => {
    const { page = 1, itemsPerPage = 20 } = options || {};

    const query = useQuery({
        queryKey: [customThemeQueryKey, page, itemsPerPage],
        staleTime: 0,
        queryFn: async () => {
            const pagination = `itemsPerPage=${itemsPerPage}&page=${page}`;
            const url = `/api/custom_themes?${pagination}`;
            const req = await loggedInApiCall.get(url);
            return req;
        },
        select: (request) => {
            const totalItems = request.data["hydra:totalItems"];
            const data = zodParseWithLog(
                request.data["hydra:member"],
                CustomThemeListSchema.array(),
                customThemeQueryKey,
            );
            return {
                items: data,
                totalItems: totalItems,
            };
        },
    });

    return {
        customThemes: query.data?.items,
        totalItems: query.data?.totalItems,
        ...query,
    };
};

export type CustomTheme = {
    "@id": string;
    id: number;
    name: string;
    description?: string | null;
    baseTheme: string;
    isActive: boolean;
    primaryColors?: Record<string, string> | null;
    secondaryColors?: Record<string, string> | null;
    typography?: Record<string, unknown> | null;
    spacing?: Record<string, unknown> | null;
    components?: Record<string, unknown> | null;
    logoUrl?: string | null;
    faviconUrl?: string | null;
    createdAt?: string;
    updatedAt?: string;
};

export const CustomThemeListSchema = z.object({
    "@id": z.string(),
    id: z.number(),
    name: z.string(),
    description: z.string().nullable().optional(),
    baseTheme: z.string(),
    isActive: z.boolean(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
});

export const CustomThemeSchema: z.ZodSchema<CustomTheme> = z
    .object({
        "@id": z.string(),
        id: z.number(),
        name: z.string(),
        description: z.string().nullable().optional(),
        baseTheme: z.string(),
        isActive: z.boolean(),
        primaryColors: z.record(z.string(), z.string()).nullable().optional(),
        secondaryColors: z.record(z.string(), z.string()).nullable().optional(),
        typography: z.record(z.string(), z.unknown()).nullable().optional(),
        spacing: z.record(z.string(), z.unknown()).nullable().optional(),
        components: z.record(z.string(), z.unknown()).nullable().optional(),
        logoUrl: z.string().nullable().optional(),
        faviconUrl: z.string().nullable().optional(),
        createdAt: z.string().optional(),
        updatedAt: z.string().optional(),
    })
    .passthrough();

export type CustomThemeListItem = z.infer<typeof CustomThemeListSchema>;
